import { ProjectId } from "@taskin-code/contracts";
import { useCallback, useEffect, useMemo, useState } from "react";

import { useAppSettings } from "./appSettings";
import {
  computeCrossProjectAttention,
  selectRelatedProjectThreads,
} from "./components/Sidebar.focus.logic";
import { nextSnoozeExpiryMs, type ThreadInboxInput } from "./lib/threadInboxState";
import { useStore } from "./store";
import type { Project } from "./types";

type ProjectWithOptionalDeletedAt = Project & {
  deletedAt?: string | null;
};

export function shouldClearFocusedProject({
  focusedProjectId,
  focusedProject,
  snapshotLoaded,
}: {
  focusedProjectId: string | null;
  focusedProject: Project | null;
  snapshotLoaded: boolean;
}): boolean {
  return snapshotLoaded && focusedProjectId !== null && focusedProject === null;
}

export function resolveFocusedProject(
  projects: readonly Project[],
  focusedProjectId: string | null,
): Project | null {
  if (focusedProjectId === null) {
    return null;
  }

  return (
    projects.find((project) => {
      const candidate = project as ProjectWithOptionalDeletedAt;
      return candidate.id === focusedProjectId && candidate.deletedAt == null;
    }) ?? null
  );
}

export function useFocusedProject() {
  const { settings, updateSettings } = useAppSettings();
  const projects = useStore((state) => state.projects);
  // `threadsHydrated` becomes true when the orchestration snapshot has been applied.
  const snapshotLoaded = useStore((state) => state.threadsHydrated);
  const focusedProjectId = settings.focusedProjectId
    ? ProjectId.makeUnsafe(settings.focusedProjectId)
    : null;
  const focusedProject = resolveFocusedProject(projects, settings.focusedProjectId);

  useEffect(() => {
    if (
      shouldClearFocusedProject({
        focusedProjectId: settings.focusedProjectId,
        focusedProject,
        snapshotLoaded,
      })
    ) {
      updateSettings({ focusedProjectId: null });
    }
  }, [focusedProject, settings.focusedProjectId, snapshotLoaded, updateSettings]);

  const setFocusedProject = useCallback(
    (id: ProjectId | null) => {
      updateSettings({ focusedProjectId: id });
    },
    [updateSettings],
  );

  return { focusedProject, focusedProjectId, setFocusedProject } as const;
}

/** Re-renders attention exactly when a snooze can become visible again. */
export function useSnoozeExpiryTick(threads: readonly ThreadInboxInput[]): number {
  const [now, setNow] = useState(() => Date.now());
  const nextExpiryMs = nextSnoozeExpiryMs(threads, now);

  useEffect(() => {
    if (nextExpiryMs === null) return;
    const delay = Math.max(0, nextExpiryMs - Date.now()) + 1;
    const timer = window.setTimeout(() => setNow(Date.now()), delay);
    return () => window.clearTimeout(timer);
  }, [nextExpiryMs]);

  return now;
}

/** Shared source of cross-project attention for the sidebar and header controls. */
export function useCrossProjectAttention() {
  const projects = useStore((state) => state.projects);
  const threads = useStore((state) => state.threads);
  const summariesById = useStore((state) => state.sidebarThreadSummaryById);
  const { focusedProject } = useFocusedProject();
  const nowMs = useSnoozeExpiryTick(threads);

  return useMemo(() => {
    const relatedThreadIds = new Set(
      focusedProject
        ? selectRelatedProjectThreads({
            threads,
            projects,
            focusedProjectId: focusedProject.id,
          }).flatMap(({ rows }) => rows.map(({ thread }) => thread.id))
        : [],
    );
    return computeCrossProjectAttention({
      threads,
      summariesById,
      projects,
      focusedProjectId: focusedProject?.id ?? null,
      excludeThreadIds: relatedThreadIds,
      nowMs,
    });
  }, [focusedProject, nowMs, projects, summariesById, threads]);
}
